import { useAuth } from "@clerk/tanstack-react-start";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { useServerFn } from "@tanstack/react-start";
import { Check } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import type { ZodIssue } from "zod";

import type { UserSelect } from "~/../drizzle/schema";
import { Button } from "~/components/ui/button";
import { DiscoveryStep } from "~/components/ui/discovery-step";
import NotFoundGraphicUrl from "~/img/not-found-telescope.svg?url";
import ServerErrorGraphicUrl from "~/img/server-error.svg?url";
import {
  findFirstInvalidStep,
  isDiscoveryStateDefault,
  resetDiscoveryState,
  useDiscoveryState,
  validateDiscoveryForm,
} from "~/lib/discovery/state";
import { languages } from "~/lib/languages";
import {
  type CoachDiscoveryField,
  type CoachDiscoveryForm,
  CoachDiscoverySchema,
  coachDiscoveryStepFieldSets,
  coachDiscoverySteps,
} from "~/lib/schemas/coach-discovery";
import { buildEmbeddingPrompt } from "~/lib/shared/embedding-prompts";
import { createNoIndexMeta } from "~/lib/shared/meta";

const easeOutExpo = (t: number): number => {
  return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
};

const fieldToStepIndex = new Map<CoachDiscoveryField, number>();
coachDiscoveryStepFieldSets.forEach((fields, index) => {
  fields.forEach((field) => fieldToStepIndex.set(field, index));
});

const DISCOVERY_COMPLETION_TAG = "action/discovery-completed" as const;

const sanitizePreferences = (form: CoachDiscoveryForm): CoachDiscoveryForm => {
  const trimmedOtherGoal = form.otherGoal?.trim() || undefined;
  const trimmedOtherFaith = form.otherFaithIntegration?.trim() || undefined;
  const requiresLocation = form.prefersCoachLocation === "Yes";

  return {
    ...form,
    otherGoal: trimmedOtherGoal,
    otherFaithIntegration: trimmedOtherFaith,
    coachLocationCountry: requiresLocation
      ? form.coachLocationCountry
      : undefined,
    coachLocationRegion: requiresLocation
      ? form.coachLocationRegion
      : undefined,
  };
};

export const saveUserPreferences = createServerFn({ method: "POST" })
  .validator(CoachDiscoverySchema)
  .handler(async ({ data: values }) => {
    const embeddingPrompt = buildEmbeddingPrompt(values);

    try {
      const [
        { getEmbeddingsClient },
        { findCoachMatches },
        { saveDiscoveryPreferences },
        { admitDiscoveryRun, DISCOVERY_RATE_LIMIT_MESSAGE },
      ] = await Promise.all([
        import("~/lib/server/embeddings-client"),
        import("~/lib/server/coach-matching"),
        import("~/lib/server/discovery-visitor"),
        import("~/lib/server/discovery-rate-limit"),
      ]);

      if (!(await admitDiscoveryRun())) {
        return {
          status: "error",
          message: DISCOVERY_RATE_LIMIT_MESSAGE,
          data: { coachName: null },
        };
      }

      const embedding =
        await getEmbeddingsClient().createEmbedding(embeddingPrompt);

      const tagDiscoveryCompletion = async (user: UserSelect) => {
        const [{ resolveUserEmail }, { tagContactInPrimaryLocation }] =
          await Promise.all([
            import("~/lib/server/user-email"),
            import("~/lib/server/ghl-contact-tags"),
          ]);

        const email = await resolveUserEmail(user);

        if (!email) {
          console.warn(
            "[saveUserPreferences] Skipping GHL tagging because email is missing",
            { userId: user.id },
          );
          return;
        }

        await tagContactInPrimaryLocation({
          contact: {
            email,
            firstName: user.firstName,
            lastName: user.lastName,
          },
          tags: [DISCOVERY_COMPLETION_TAG],
        });
      };

      const getCoaches = async () => {
        // Only fetch 3 coaches - we just need the one at index 2 for navigation
        const coaches = await findCoachMatches({
          preferences: { ...values, embedding },
          limit: 3,
        });

        if (coaches.length < 3) {
          // Not enough coaches, fall back to first available
          return coaches[0]?.coach.username ?? null;
        }

        // Return the coach at index 2 (first enabled for anonymous users)
        return coaches[2].coach.username ?? coaches[0].coach.username ?? null;
      };

      const user = await saveDiscoveryPreferences({
        ...values,
        preferencesEmbedding: embedding,
      });

      const coachName = await getCoaches();

      try {
        await tagDiscoveryCompletion(user);
      } catch (tagError) {
        console.warn(
          "[saveUserPreferences] GHL tagging failed (non-critical)",
          {
            userId: user.id,
            error:
              tagError instanceof Error ? tagError.message : String(tagError),
          },
        );
      }

      if (coachName) {
        return {
          status: "success",
          message: "Preferences processed successfully",
          data: { coachName },
        };
      }
      return {
        status: "not_found",
        message: "No suitable coaches found",
        data: { coachName: null },
      };
    } catch (error) {
      // Report the error itself, on the server, while it is still whole.
      //
      // The `console.error` this replaces did reach Sentry — the Worker runs
      // `captureConsoleIntegration({ levels: ["error"] })` — but only as a
      // synthesized *message* event: no argument was an `Error` instance, so
      // the event carried no exception and no stack, and `safeJoin` collapsed
      // the details object to "[object Object]". Every cause in the funnel
      // (embeddings, the preferences write, coach matching, a Neon drop) landed
      // in one issue under that same constant title.
      //
      // `reportToSentry` groups on the real stack instead, and it reads
      // postgres fields off the error, so a dropped Hyperdrive/Neon connection
      // is fingerprinted into the shared `postgres/connection` bucket.
      const { reportToSentry } = await import("~/lib/server/sentry-report");
      reportToSentry(error, "server_function");

      // Still resolve rather than reject: nothing about the failure should
      // cross the RPC boundary to an anonymous visitor, and the client renders
      // the same "Processing Error" card for this status as for a rejection.
      return {
        status: "error",
        message: "Failed to save preferences",
        data: { coachName: null },
      };
    }
  });

interface StepPreference {
  progress: number;
  preference: {
    id: string;
    label: string;
    value: string | string[];
  };
}

type ProcessingState =
  | { type: "validating" }
  | { type: "invalid"; issues: ZodIssue[] }
  | { type: "processing"; progress: number }
  | { type: "not_found"; message: string }
  | { type: "error"; message: string };

function ProcessingPage() {
  const { isSignedIn } = useAuth();
  const navigate = useNavigate({ from: Route.fullPath });
  const [state, setState] = useState<ProcessingState>({ type: "validating" });
  const [displayPercentage, setDisplayPercentage] = useState(0);
  const serverCallStarted = useRef(false);
  const animationDuration = 6000;
  const { form: discoveryForm } = useDiscoveryState();
  const [validatedPreferences, setValidatedPreferences] =
    useState<CoachDiscoveryForm | null>(null);
  const savePreferences = useServerFn(saveUserPreferences);
  const preferenceSource = useMemo(
    () => sanitizePreferences(validatedPreferences ?? discoveryForm),
    [validatedPreferences, discoveryForm],
  );
  const fallbackStep = useMemo(
    () => findFirstInvalidStep(preferenceSource),
    [preferenceSource],
  );
  const validationIssues = useMemo(() => {
    if (state.type !== "invalid") {
      return [] as Array<{
        field: string | null;
        message: string;
        stepNumber: number | null;
        stepTitle?: string;
      }>;
    }

    return state.issues.map((issue) => {
      const field = typeof issue.path[0] === "string" ? issue.path[0] : null;
      const stepIndex =
        field && fieldToStepIndex.has(field as CoachDiscoveryField)
          ? (fieldToStepIndex.get(field as CoachDiscoveryField) ?? undefined)
          : undefined;
      const stepNumber =
        typeof stepIndex === "number" ? stepIndex + 1 : (fallbackStep ?? null);
      const stepTitle =
        typeof stepIndex === "number"
          ? coachDiscoverySteps[stepIndex].title
          : undefined;

      return {
        field,
        message: issue.message,
        stepNumber,
        stepTitle,
      };
    });
  }, [fallbackStep, state]);
  const firstNavigableStep = useMemo(() => {
    return (
      validationIssues.find((issue) => issue.stepNumber !== null)?.stepNumber ??
      fallbackStep ??
      1
    );
  }, [validationIssues, fallbackStep]);

  const processingStepsText = [
    { text: "Analyzing your preferences...", threshold: 10 },
    { text: "Finding the best coaches...", threshold: 40 },
    { text: "Tailoring your matches...", threshold: 75 },
    { text: "Finalizing results...", threshold: 95 },
  ];
  const currentProcessingStepIndex = useMemo(() => {
    return processingStepsText.findIndex(
      (step, index) =>
        displayPercentage < step.threshold ||
        index === processingStepsText.length - 1,
    );
  }, [displayPercentage]);

  const preferenceSteps = useMemo(() => {
    if (!preferenceSource) {
      return [];
    }

    const rawPrefs = preferenceSource;
    const steps: StepPreference[] = [];

    // Add coaching format
    if (rawPrefs.coachingFormats) {
      steps.push({
        progress: 10,
        preference: {
          id: "type",
          label: "Type",
          value: rawPrefs.coachingFormats,
        },
      });
    }

    // Add session delivery method
    if (rawPrefs.sessionDeliveryMethods) {
      steps.push({
        progress: 20,
        preference: {
          id: "format",
          label: "Format",
          value: rawPrefs.sessionDeliveryMethods,
        },
      });
    }

    // Add goals
    if (rawPrefs.goals?.length) {
      rawPrefs.goals.forEach((goal: string, index: number) => {
        steps.push({
          progress: 30 + index * 5,
          preference: {
            id: `goal-${index}`,
            label: `Goal ${index + 1}`,
            value: goal,
          },
        });
      });
    }

    // Add coach gender
    if (rawPrefs.coachGender && rawPrefs.coachGender !== "No preference") {
      steps.push({
        progress: 50,
        preference: {
          id: "gender",
          label: "Gender",
          value: rawPrefs.coachGender,
        },
      });
    }

    // Add preferred languages
    if (rawPrefs.preferredLanguages?.length) {
      rawPrefs.preferredLanguages.forEach((langCode: string, index: number) => {
        const lang = languages.find((l) => l.name === langCode);
        if (lang) {
          steps.push({
            progress: 60 + index * 5,
            preference: {
              id: `lang-${index}`,
              label: "Language",
              value: lang.native,
            },
          });
        }
      });
    }

    // Add coaching approaches
    if (rawPrefs.coachingApproaches?.length) {
      steps.push({
        progress: 75,
        preference: {
          id: "approaches",
          label: "Approaches",
          value: rawPrefs.coachingApproaches.join(", "),
        },
      });
    }

    // Add coaching packages
    if (rawPrefs.coachingPackages?.length) {
      steps.push({
        progress: 85,
        preference: {
          id: "packages",
          label: "Experience",
          value: rawPrefs.coachingPackages.join(", "),
        },
      });
    }

    // Add faith integration
    if (rawPrefs.faithIntegration?.length) {
      steps.push({
        progress: 90,
        preference: {
          id: "faith",
          label: "Faith Integration",
          value: rawPrefs.faithIntegration.join(", "),
        },
      });
    }

    steps.sort((a, b) => (a.progress ?? 0) - (b.progress ?? 0));

    return steps;
  }, [preferenceSource]);

  const visiblePreferenceSteps = useMemo(() => {
    const visible = preferenceSteps.filter(
      (step) => displayPercentage >= (step.progress ?? 0),
    );
    return visible;
  }, [preferenceSteps, displayPercentage]);

  const navigateToDiscovery = useCallback(() => {
    navigate({ to: "/discovery" });
  }, [navigate]);

  // Validation effect - runs once on mount
  useEffect(() => {
    if (state.type !== "validating") {
      return;
    }

    if (isDiscoveryStateDefault(discoveryForm)) {
      navigateToDiscovery();
      return;
    }

    const validation = validateDiscoveryForm(preferenceSource);

    if (!validation.success) {
      setValidatedPreferences(null);
      setState({ type: "invalid", issues: validation.error.issues });
      return;
    }

    setValidatedPreferences(sanitizePreferences(validation.data));
    setState({ type: "processing", progress: 0 });
  }, [state.type, discoveryForm, preferenceSource, navigateToDiscovery]);

  // Server processing effect - runs once when processing starts
  useEffect(() => {
    if (
      state.type !== "processing" ||
      !validatedPreferences ||
      serverCallStarted.current
    ) {
      return;
    }

    serverCallStarted.current = true;

    const processPreferences = async () => {
      const payload = sanitizePreferences(validatedPreferences);

      try {
        localStorage.setItem("userPreferences", JSON.stringify(payload));
        localStorage.removeItem("discoveryFormData");

        const result = await savePreferences({ data: payload });

        resetDiscoveryState();

        if (result.status === "success" && result.data.coachName) {
          // Navigate immediately - no intermediate success state needed
          if (isSignedIn) {
            navigate({ to: "/coach-finder" });
          } else {
            navigate({
              to: "/discovery/$username",
              params: { username: result.data.coachName },
            });
          }
        } else if (result.status === "not_found") {
          const message = result.message || "No suitable coaches found.";
          setState({ type: "not_found", message });
          toast.info(message);
        } else {
          const message = result.message || "An unexpected error occurred.";
          setState({ type: "error", message });
          toast.error(message);
        }
      } catch (error) {
        console.error("Failed to process preferences in discovery flow", {
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
          timestamp: new Date().toISOString(),
          hasPreferences: true,
          context: "discovery/processing/processPreferences",
        });

        const message = "Failed to save preferences";
        setState({ type: "error", message });
        toast.error(message);
      }
    };

    processPreferences();
  }, [state.type, validatedPreferences, savePreferences]);

  // Animation effect - runs independently
  useEffect(() => {
    if (state.type !== "processing") {
      return;
    }

    let startTime: number | null = null;
    let animationFrameId: number;

    const animate = (timestamp: number) => {
      if (!startTime) {
        startTime = timestamp;
      }
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / animationDuration, 1);
      const easedProgress = easeOutExpo(progress);
      const newPercentage = Math.min(Math.floor(easedProgress * 100), 100);

      setDisplayPercentage(newPercentage);
      setState((prev) =>
        prev.type === "processing"
          ? { ...prev, progress: newPercentage }
          : prev,
      );

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      }
    };

    animationFrameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrameId);
  }, [state.type, animationDuration]);

  const renderContent = () => {
    switch (state.type) {
      case "validating":
        return (
          <div className="text-lg text-gray-600">
            Validating your preferences...
          </div>
        );

      case "invalid":
        return (
          <div className="space-y-6 text-left">
            <div>
              <h2 className="font-heading text-2xl font-semibold text-gray-900">
                We found a few things to update
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Fix the items below so we can finish processing your matches.
              </p>
            </div>
            <ul className="space-y-3">
              {validationIssues.map(
                ({ field, message, stepNumber, stepTitle }, index) => (
                  <li
                    key={`${field ?? index}-${message}`}
                    className="rounded-lg bg-gray-50 p-4 text-sm text-gray-700"
                  >
                    <span className="block font-semibold text-gray-900">
                      {stepNumber ? `Step ${stepNumber}` : "Discovery form"}
                      {stepTitle ? ` · ${stepTitle}` : null}
                    </span>
                    <span>{message}</span>
                  </li>
                ),
              )}
            </ul>
            <div className="flex gap-3">
              <Button
                type="button"
                onClick={() =>
                  navigate({ to: `/discovery/steps/${firstNavigableStep}` })
                }
              >
                Go to Step {firstNavigableStep}
              </Button>
              <Button type="button" onClick={navigateToDiscovery}>
                Restart Discovery
              </Button>
            </div>
          </div>
        );

      case "processing":
        return (
          <>
            <div className="relative h-32 w-32">
              <svg
                className="absolute top-0 left-0 h-full w-full -rotate-90 transform"
                viewBox="0 0 100 100"
              >
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="10"
                  className="text-gray-200"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="10"
                  strokeDasharray="283"
                  strokeDashoffset={283 - (displayPercentage / 100) * 283}
                  className="text-brand-700 transition-[stroke-dashoffset] duration-300 ease-linear"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center text-3xl font-semibold tabular-nums">
                {displayPercentage}%
              </div>
            </div>
            <p className="text-lg text-gray-600">
              {processingStepsText[Math.max(0, currentProcessingStepIndex - 1)]
                ?.text || "Processing..."}
            </p>
            <div className="mt-4 w-full max-w-sm space-y-2">
              {visiblePreferenceSteps.map((step, index) => (
                <div
                  key={`${step.preference?.id}-${index}`}
                  className="flex items-center justify-start gap-2 rounded-md bg-gray-100 px-4 py-2 text-sm duration-500 animate-in fade-in"
                >
                  <span className="font-medium text-gray-600">
                    {step.preference?.label || "Preference"}:
                  </span>
                  <span className="truncate text-gray-800">
                    {step.preference?.value || "Loading..."}
                  </span>
                  <Check className="ml-2 h-4 w-4 flex-shrink-0 text-green-500" />
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm text-gray-500">
              Hang tight, we're finding the best coaches for you!
            </p>
          </>
        );

      case "not_found":
        return (
          <div className="flex flex-col items-center gap-4">
            <div className="h-40 w-40 text-gray-400">
              <img
                src={NotFoundGraphicUrl}
                alt="No matches found"
                className="h-full w-full"
              />
            </div>
            <h2 className="text-xl font-semibold">No Matches Found</h2>
            <p className="text-gray-600">
              We couldn't find coaches perfectly matching all your criteria at
              this moment.
            </p>
            <p className="text-sm text-gray-500">
              Try adjusting your preferences to find more matches.
            </p>
            <Button
              type="button"
              onClick={() => {
                resetDiscoveryState();
                navigateToDiscovery();
              }}
              className="mt-2"
            >
              Start New Search
            </Button>
          </div>
        );

      case "error":
        return (
          <div className="flex flex-col items-center gap-4">
            <div className="h-40 w-40 text-red-400">
              <img
                src={ServerErrorGraphicUrl}
                alt="Server error"
                className="h-full w-full"
              />
            </div>
            <h2 className="text-xl font-semibold text-red-600">
              Processing Error
            </h2>
            <p className="text-gray-600">{state.message}</p>
            <p className="text-sm text-gray-500">
              Please try again later or contact support.
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <DiscoveryStep.Card>
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-8 text-center">
        {renderContent()}
      </div>
    </DiscoveryStep.Card>
  );
}

export const Route = createFileRoute("/_public/discovery/steps/processing/")({
  head: () =>
    createNoIndexMeta(
      "Processing Your Preferences – Dream Coach Match",
      "Finding the perfect coach for you based on your discovery responses",
    ),
  component: ProcessingPage,
});
